// src/components/settings/RegionManagement.tsx
//
// Settings → Regions: the org admin's region hierarchy editor (Story 1.1).
// Indented tree with inline add-child/rename, a "Move under…" picker
// (no drag-and-drop), and delete guarded by the DB. All data access goes
// through useRegions → regionsService; the tree re-renders only from the
// server's answer (no optimistic updates).

import React, { useMemo, useRef, useState } from 'react'
import { Plus, Pencil, FolderTree, Trash2 } from 'lucide-react'
import { Button } from '../ui/Button'
import { useRegions } from '../../hooks/useRegions'
import {
  buildRegionTree,
  flattenRegionTree,
  collectSubtreeIds,
} from '../../utils/regionTree'
import type { YakRegion } from '../../types/yak'

type EditorState =
  | { mode: 'add-root' }
  | { mode: 'add-child'; parentId: string }
  | { mode: 'rename'; regionId: string; initial: string }

export function RegionManagement() {
  const { regions, loading, error, create, rename, move, remove } = useRegions()

  const [editor, setEditor] = useState<EditorState | null>(null)
  const [draftName, setDraftName] = useState('')
  const [moveTarget, setMoveTarget] = useState<YakRegion | null>(null)
  const [moveSelection, setMoveSelection] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<YakRegion | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  // Focus returns to the control that opened an overlay/editor when it
  // closes. When that control no longer exists (e.g. the row was deleted, or
  // an inline editor replaced its own Rename trigger), focus falls back to
  // the section heading rather than dropping to <body> — WCAG 2.2 AA floor.
  const overlayTriggerRef = useRef<HTMLElement | null>(null)
  const headingRef = useRef<HTMLHeadingElement | null>(null)

  const restoreFocus = (trigger: HTMLElement | null) => {
    if (trigger && document.contains(trigger)) {
      trigger.focus()
    } else {
      headingRef.current?.focus()
    }
  }

  const rows = useMemo(
    () => flattenRegionTree(buildRegionTree(regions)),
    [regions]
  )
  const parentIds = useMemo(
    () =>
      new Set(
        regions
          .map(r => r.parent_region_id)
          .filter((id): id is string => id !== null)
      ),
    [regions]
  )

  const openEditor = (state: EditorState) => {
    // Remember the control that opened the inline editor so focus can return
    // to it on close. (For rename the trigger is replaced by the editor, so
    // restoreFocus falls back to the heading.)
    overlayTriggerRef.current = document.activeElement as HTMLElement | null
    setEditor(state)
    setDraftName(state.mode === 'rename' ? state.initial : '')
    setActionError(null)
  }

  const closeEditor = () => {
    const trigger = overlayTriggerRef.current
    overlayTriggerRef.current = null
    setEditor(null)
    setDraftName('')
    restoreFocus(trigger)
  }

  const runMutation = async (fn: () => Promise<void>, onDone: () => void) => {
    setPending(true)
    setActionError(null)
    try {
      await fn()
      onDone()
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Something went wrong'
      )
    } finally {
      setPending(false)
    }
  }

  const submitEditor = () => {
    if (!editor || pending) return
    const name = draftName.trim()
    if (!name) return
    if (editor.mode === 'rename') {
      runMutation(() => rename(editor.regionId, name), closeEditor)
    } else {
      const parentId = editor.mode === 'add-child' ? editor.parentId : null
      runMutation(() => create(name, parentId), closeEditor)
    }
  }

  const openOverlay = (
    trigger: React.MouseEvent<HTMLElement>,
    open: () => void
  ) => {
    overlayTriggerRef.current = trigger.currentTarget
    setActionError(null)
    open()
  }

  const closeOverlay = (close: () => void) => {
    const trigger = overlayTriggerRef.current
    overlayTriggerRef.current = null
    close()
    // After a delete the triggering row is gone; restoreFocus falls back to
    // the heading instead of focusing a detached node.
    restoreFocus(trigger)
  }

  const confirmMove = () => {
    if (!moveTarget || pending) return
    const newParentId = moveSelection === 'root' ? null : moveSelection
    if (newParentId === null && moveSelection !== 'root') return
    runMutation(
      () => move(moveTarget.id, newParentId),
      () => closeOverlay(() => setMoveTarget(null))
    )
  }

  const confirmDelete = () => {
    if (!deleteTarget || pending) return
    runMutation(
      () => remove(deleteTarget.id),
      () => closeOverlay(() => setDeleteTarget(null))
    )
  }

  const editorRow = (depth: number) => (
    <div
      className="flex items-center gap-2 py-1.5"
      style={{ paddingLeft: `${depth * 24}px` }}
    >
      <input
        autoFocus
        aria-label="Region name"
        aria-describedby={actionError ? 'region-action-error' : undefined}
        className="block w-64 px-3 py-1.5 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-brand-500 focus:border-brand-500 sm:text-sm"
        placeholder="Region name"
        value={draftName}
        onChange={e => setDraftName(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') submitEditor()
          if (e.key === 'Escape') closeEditor()
        }}
      />
      <Button
        size="sm"
        variant="outline"
        onClick={submitEditor}
        loading={pending}
        disabled={!draftName.trim()}
      >
        Save
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={closeEditor}
        disabled={pending}
      >
        Cancel
      </Button>
    </div>
  )

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Regions</h2>
        <div className="space-y-3" aria-hidden="true">
          {[0, 1, 2].map(i => (
            <div
              key={i}
              className="h-8 bg-gray-100 rounded animate-pulse"
              style={{ marginLeft: `${i * 24}px` }}
            />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="md:hidden mb-4 bg-gray-50 border border-gray-200 text-gray-600 rounded-lg px-4 py-3 text-sm">
        The region editor works best on a larger screen.
      </div>

      <div className="flex justify-between items-center mb-4">
        <div>
          <h2
            ref={headingRef}
            tabIndex={-1}
            className="text-xl font-bold text-gray-900 focus:outline-none"
          >
            Regions
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Reps and market-access users are assigned to regions; routing
            resolves through this tree.
          </p>
        </div>
        {regions.length > 0 && !editor && (
          <Button size="sm" onClick={() => openEditor({ mode: 'add-root' })}>
            <Plus className="w-4 h-4 mr-1" />
            Add region
          </Button>
        )}
      </div>

      {error && (
        <div
          role="alert"
          className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm"
        >
          {error}
        </div>
      )}
      {actionError && !moveTarget && !deleteTarget && (
        <div
          id="region-action-error"
          role="alert"
          className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm"
        >
          {actionError}
        </div>
      )}

      {regions.length === 0 && !editor ? (
        <div className="text-center py-10">
          <FolderTree className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 mb-4">
            Your organization has no regions yet.
          </p>
          <Button onClick={() => openEditor({ mode: 'add-root' })}>
            Create the first region
          </Button>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {rows.map(({ region, depth }) => {
            const hasChildren = parentIds.has(region.id)
            const isRenaming =
              editor?.mode === 'rename' && editor.regionId === region.id

            return (
              <React.Fragment key={region.id}>
                {isRenaming ? (
                  editorRow(depth)
                ) : (
                  <div
                    className="group flex items-center justify-between py-1.5"
                    style={{ paddingLeft: `${depth * 24}px` }}
                  >
                    <span className="text-sm text-gray-900">{region.name}</span>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          openEditor({ mode: 'add-child', parentId: region.id })
                        }
                      >
                        <Plus className="w-3.5 h-3.5 mr-1" />
                        Add child
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          openEditor({
                            mode: 'rename',
                            regionId: region.id,
                            initial: region.name,
                          })
                        }
                      >
                        <Pencil className="w-3.5 h-3.5 mr-1" />
                        Rename
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={e =>
                          openOverlay(e, () => {
                            setMoveTarget(region)
                            setMoveSelection(null)
                          })
                        }
                      >
                        Move under…
                      </Button>
                      {hasChildren ? (
                        <span className="text-xs text-gray-500 px-2">
                          Has child regions — move or delete them first
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={e =>
                            openOverlay(e, () => setDeleteTarget(region))
                          }
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" />
                          Delete
                        </Button>
                      )}
                    </div>
                  </div>
                )}
                {editor?.mode === 'add-child' &&
                  editor.parentId === region.id &&
                  editorRow(depth + 1)}
              </React.Fragment>
            )
          })}
          {editor?.mode === 'add-root' && editorRow(0)}
        </div>
      )}

      {/* "Move under…" parent picker */}
      {moveTarget && (
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50 p-4"
          onKeyDown={e => {
            if (e.key === 'Escape') closeOverlay(() => setMoveTarget(null))
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="move-region-title"
            className="bg-white rounded-lg shadow-lg max-w-md w-full p-6"
          >
            <h3
              id="move-region-title"
              className="text-lg font-semibold text-gray-900 mb-1"
            >
              Move “{moveTarget.name}”
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Its child regions move with it. It can’t be moved under itself or
              one of its own descendants.
            </p>
            {actionError && (
              <div
                role="alert"
                className="mb-3 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm"
              >
                {actionError}
              </div>
            )}
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100 mb-4">
              <button
                autoFocus
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                  moveSelection === 'root'
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-gray-900'
                }`}
                onClick={() => setMoveSelection('root')}
              >
                Top level (no parent)
              </button>
              {(() => {
                const excluded = collectSubtreeIds(regions, moveTarget.id)
                return rows
                  .filter(({ region }) => !excluded.has(region.id))
                  .map(({ region, depth }) => (
                    <button
                      key={region.id}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                        moveSelection === region.id
                          ? 'bg-brand-50 text-brand-700'
                          : 'text-gray-900'
                      }`}
                      style={{ paddingLeft: `${12 + depth * 20}px` }}
                      onClick={() => setMoveSelection(region.id)}
                    >
                      {region.name}
                    </button>
                  ))
              })()}
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => closeOverlay(() => setMoveTarget(null))}
                disabled={pending}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={confirmMove}
                loading={pending}
                disabled={moveSelection === null}
              >
                Move
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50 p-4"
          onKeyDown={e => {
            if (e.key === 'Escape') closeOverlay(() => setDeleteTarget(null))
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-region-title"
            className="bg-white rounded-lg shadow-lg max-w-md w-full p-6"
          >
            <h3
              id="delete-region-title"
              className="text-lg font-semibold text-gray-900 mb-1"
            >
              Delete “{deleteTarget.name}”
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              This removes the region permanently. Reps, assignments and routing
              will no longer be able to reference it.
            </p>
            {actionError && (
              <div
                role="alert"
                className="mb-3 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm"
              >
                {actionError}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button
                autoFocus
                variant="ghost"
                onClick={() => closeOverlay(() => setDeleteTarget(null))}
                disabled={pending}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={confirmDelete}
                loading={pending}
              >
                Delete region
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
